import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, Typography, message } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  MobileOutlined,
  SafetyOutlined,
  AlertOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons';
import ContentSkeleton from '../../components/common/ContentSkeleton';
import ErrorState from '../../components/common/ErrorState';
import { dashboardApi } from '../../services/api';
import type { SellerDashboardInfo } from '../../types/seller.types';

const { Title } = Typography;

const DashboardPage: React.FC = () => {
  const { isLoading, error, permissions, user } = useSelector((state: RootState) => state.auth);
  const [dashboardData, setDashboardData] = useState<SellerDashboardInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dashboardResponse = await dashboardApi.getSellerDashboardInfo();

        if (dashboardResponse.status === "SUCCESS") {
          setDashboardData(dashboardResponse.data);
        } else {
          message.error(dashboardResponse.message || "Failed to fetch dashboard data");
        }
      } catch (error) {
        message.error("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);



  if (error) {
    return <ErrorState error={error} onRetry={() => window.location.reload()} />;
  }

  if (isLoading || loading) {
    return <ContentSkeleton cardCount={4} rows={4} hasTable />;
  }

  // Use dashboard data for metrics
  const totalPurchasesThisMonth = dashboardData?.purchaseCurrentMonth || 0;
  const totalDevicesRegisteredThisMonth = dashboardData?.deviceRegistrationCurrentMonth || 0;
  const totalPurchase = dashboardData?.totalPurchase || 0;

  return (
    <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
      <Title level={2} style={{ marginBottom: 24 }}>Dashboard Overview</Title>
      
      <Row gutter={[16, 16]}>
        {[
          {
            title: "Token Balance",
            value: dashboardData?.tokenBalance || 0,
            icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
            color: '#52c41a'
          },
          {
            title: "Total Devices",
            value: dashboardData?.totalDevices || 0,
            icon: <MobileOutlined />,
            color: '#1890ff'
          },
          {
            title: "Locked Devices",
            value: dashboardData?.lockedDevices || 0,
            icon: <AlertOutlined style={{ color: '#faad14' }} />,
            color: '#faad14'
          },
          {
            title: "Today's Activations",
            value: dashboardData?.todayActivation || 0,
            icon: <SafetyOutlined style={{ color: '#ff4d4f' }} />,
            color: '#ff4d4f'
          }
        ].map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card 
              hoverable
              style={{ 
                transition: 'all 0.3s ease',
                animation: `fadeInUp 0.5s ease-out ${index * 0.1}s`
              }}
            >
              <Statistic
                title={stat.title}
                value={stat.value}
                prefix={stat.icon}
                valueStyle={{ color: stat.color }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Additional Metrics */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {[
          {
            title: "Monthly Purchases",
            value: totalPurchasesThisMonth,
            icon: <ShoppingCartOutlined style={{ color: '#1890ff' }} />,
            color: '#1890ff'
          },
          {
            title: "Monthly Device Registrations",
            value: totalDevicesRegisteredThisMonth,
            icon: <MobileOutlined style={{ color: '#faad14' }} />,
            color: '#faad14'
          },
          {
            title: "Total Purchase",
            value: totalPurchase,
            icon: <DollarOutlined style={{ color: '#52c41a' }} />,
            color: '#52c41a'
          }
        ].map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card 
              hoverable
              style={{ 
                transition: 'all 0.3s ease',
                animation: `fadeInUp 0.5s ease-out ${(index + 4) * 0.1}s`
              }}
            >
              <Statistic
                title={stat.title}
                value={stat.value}
                prefix={stat.icon}
                valueStyle={{ color: stat.color }}
              />
            </Card>
          </Col>
        ))}
      </Row>





      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(40px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .ant-card {
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }

          .ant-card:hover {
            box-shadow: 0 4px 16px rgba(0,0,0,0.15);
            transform: translateY(-2px);
          }

          .ant-table-thead > tr > th {
            background-color: #fafafa !important;
            font-weight: bold !important;
          }

          /* Mobile responsiveness */
          @media (max-width: 768px) {
            .ant-card .ant-card-body {
              padding: 12px !important;
            }
            
            .ant-statistic-title {
              font-size: 12px !important;
            }
            
            .ant-statistic-content {
              font-size: 18px !important;
            }
          }

          @media (max-width: 576px) {
            .ant-card .ant-card-body {
              padding: 8px !important;
            }
            
            .ant-statistic-title {
              font-size: 11px !important;
            }
            
            .ant-statistic-content {
              font-size: 16px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default DashboardPage;